import { IConnection } from "./Connection";
import * as c from '@iamo/custody-lib';
import BigNumber from 'bignumber.js';

export enum Operation {
    Call,
    DelegateCall,
}

export enum Executor {
    NotSet,
    SafeDirectly,
    SafeOverRelay,
    AllowlistModule,
    LimitModule,
}

export enum SignatureType {
    ECDSA,
    EIP1271,
    PreValidated,
}

export enum IAMORole {
    NoRole = 0,
    Challenger = 1,
    Initiator = 2,
    Approver = 3,
}

export interface IMultisigAccount {
    address: string;
    roles: IAMORole[];
    consent?: IBunkerConsent;
}

export enum TransactionState {
    Uninitialized,
    InsufficientSigned,
    SufficientSigned,
    Pending,
    MinedWithSuccess,
    Error,
}
export interface ITransactionParameters {
    to: string;
    value: string;
    data: string;
    operation: Operation;
    safeTxGas: string;
    baseGas: string;
    gasPrice: string;
    gasToken: string;
    refundReceiver: string;
}

export interface ISignature {
    address: string;
    sigData: any;
}

/**
 * Data structure of contract signature.
 * This data will be set to ISignature.sigData
 *
 * https://docs.gnosis.io/safe/docs/contracts_signatures/#contract-signature-eip-1271
 */
export interface IContractSignatureData {
    v: number; // 00
    r: Buffer; // Signer contract address (32 bytes)
    s: Buffer; // Position of contract signature (32 bytes)
    contractSignatures: Buffer; // Owner signatures of the signer contract
}

export interface ITransactionError {
    description: string;
    preErrorTransactionState: TransactionState;
    errorObject?: any;
}

export interface IExportableTransactionParameters {
    txParameters: ITransactionParameters;
    executor?: Executor;
    name?: string;
    txHash?: string;
    instanceAddress?: string;
    nonce?: string;
    initiatorSig?: ISignature;
    sigs?: ISignature[];
    ethereumTxHash?: string;
    error?: ITransactionError;
    relatedLogs?: string[];
    transactionState: TransactionState;
    privacyWalletTx?: IExportableTransactionParameters;
}

export interface IEnrichedTransactionParameters extends IExportableTransactionParameters {
    connection: IConnection;
    multisigInstance: any;
    signatureType: SignatureType;
    signerInstance?: any;
    events?: any[];
    privacyWalletTx?: IExportableTransactionParameters;
}

export interface ISignatureOptions {
    signatureType: SignatureType;
    signerAddress?: string;
    signerContract?: { abi: any; bytecode: any };
}

export interface IBunkerConsent {
    'challenge-response'?: string;
    'policy-id'?: string;
    signature?: { value: any, timestamp: any };
}
export interface IBunkerConsentParams {
    key: string;
    secret: string;
    consent: IBunkerConsent;
}

/**
 * The transaction-class of the wallet
 */
export class Transaction {

    // The blockchain property of the backend connection object.
    connection: IConnection

    /**
     * initializes the class
     * @param connection The backend connection object.
     */
    constructor(connection: IConnection){
        this.connection = connection
    }

    /**
     * Updated the state of an existing transaction
     * @param enrichedTxParams The object, representing the transaction
     * @returns The updated enriched transaction-parameters
     */
    async updateState(
        enrichedTxParams: IEnrichedTransactionParameters)
    : Promise<IEnrichedTransactionParameters> {
        return c.Transaction.updateState(enrichedTxParams)
    }

    /**
     * Signs a transaction with the keys held in the local memory wallet.
     * @param enrichedTxParams The object, representing the transaction
     * @returns The updated enriched transaction-parameters
     */
    async signWithLocalKeys(
        enrichedTxParams: IEnrichedTransactionParameters
    ): Promise<IEnrichedTransactionParameters>{
        return c.Transaction.signWithLocalKeys(enrichedTxParams)
    }

    /**
     * Still not fully implemented
     * @param enrichedTxParams  The object representing the transaction.
     * @param consentParams The bunker key, secret, consent to sign with
     * @returns The updated enriched transaction-parameters
     */
    async sign(
        enrichedTxParams: IEnrichedTransactionParameters,
        consentParams?: IBunkerConsentParams
    ): Promise<IEnrichedTransactionParameters> {
        return c.Transaction.sign(enrichedTxParams, consentParams)
    }

    /**
     * Sends a transaction to the selected chain
     *
     * Note: If a privacy wallet tx is provided without the privacy connection
     * the controller connection will send the tx breaking the privacy link
     * @param enrichedTxParams  The object, representing the transaction.
     * @param privacyConnection The privacy wallet connection to send the transaction from
     * @returns The updated enriched transaction-parameters
     */
    async send(
        enrichedTxParams: IEnrichedTransactionParameters,
        privacyWalletConnection?: IConnection
    ): Promise<IEnrichedTransactionParameters> {
        return c.Transaction.send(enrichedTxParams,privacyWalletConnection)
    }

    /**
     * Factory function for a generic transaction object.
     * Calculates the gas values and the transaction parameter hash (that's not the ethereum tx hash).
     * @param instanceAddress The ethereum address of the wallet (multisig proxy instance).
     * @param txData The encoded transaction data.
     * @param to The ethereum address which should be called.
     * @param value The ether value in wei.
     * @param operation The type of operaion (default: `Operaion.Call`).
     * @returns The updated enriched transaction-parameters
     */
    async createGenericTransaction(
        instanceAddress: string,
        txData: string,
        to: string,
        value: string,
        operation?: Operation,
    ): Promise<IEnrichedTransactionParameters> {
        return c.Transaction.createGenericTransaction(this.connection, instanceAddress, txData, to, value, operation)
    }

    /**
     * Factory function for a value transaction object.
     * Calculates the gas values and the transaction parameter hash (that's not the ethereum tx hash).
     * @param instanceAddress The ethereum address of the wallet (multisig proxy instance).
     * @param to The ethereum address which should be called.
     * @param value The ether value in wei.
     * @returns The updated enriched transaction-parameters
     */
    async createValueTransaction(
        instanceAddress: string,
        to: string,
        value: string,
    ): Promise<IEnrichedTransactionParameters> {
        return c.Transaction.createValueTransaction(this.connection, instanceAddress, to, value)
    }

    /**
     * Imports threshold signatures to an existing object
     * @param thresholdSignatures The threshold signatures to import.
     * @param enrichedTxParams The object, representing the transaction.
     * @returns The updated enriched transaction-parameters
     */
    async importThresholdSignatures(
        thresholdSignatures: ISignature[],
        enrichedTxParams: IEnrichedTransactionParameters,
    ): Promise<IEnrichedTransactionParameters> {
        return c.Transaction.importThresholdSignatures(thresholdSignatures, enrichedTxParams)
    }

    /**
     * Imports the initiator signature to an existing object
     * @param initiatorSig The initiator-signature to import
     * @param enrichedTxParams The object, representing the transaction
     * @returns The updated enriched transaction-parameters
     */
    async importInitiatorSignature(
        initiatorSig: ISignature,
        enrichedTxParams: IEnrichedTransactionParameters
    ): Promise<IEnrichedTransactionParameters> {
        return c.Transaction.importInitiatorSignature(initiatorSig, enrichedTxParams)
    }
    
    /**
     * Exports all threshold signatures
     * @param enrichedTxParams The object, representing the transaction.
     * @returns The exported signatures
     */
    exportThresholdSignatures(
        enrichedTxParams: IEnrichedTransactionParameters
    ): ISignature[]{
        return c.Transaction.exportThresholdSignatures(enrichedTxParams)
    }

    /**
     * Exports the initiator-signature
     * @param enrichedTxParams The object, representing the transaction.
     * @returns The exported signatures
     */
    exportInitiatorSignature(
        enrichedTxParams: IEnrichedTransactionParameters,
    ): ISignature {
        return c.Transaction.exportInitiatorSignature(enrichedTxParams)
    }

    /**
     * Exports a transaction
     * @param enrichedTxParams The object, representing the transaction.
     * @returns The exported transaction parameters
     */
    exportTransaction(
        enrichedTxParams: IEnrichedTransactionParameters
    ): IExportableTransactionParameters {
        return c.Transaction.exportTransaction(enrichedTxParams)
    }

    /**
     * Gets the contractAddress's incoming transactions.
     *
     * Note: this implementation is temporary - this feature should be in the bunker with dedicated database.
     * @param contractAddress The contract address to fetch transaction history
     * @param tokenAddresses The ERC20-token-address to check 
     * @param fromBlock The starting-blocknumber for the check
     * @param toBlock The ending-blocknumber for the check
     * @returns The incoming transactions
     */
    async getIncomingTransfer(
        contractAddress: string,
        tokenAddresses: string[],
        fromBlock: number,
        toBlock: number
    ): Promise<
            Array<{
                transactionHash: string;
                blockNumber: number;
                from: string;
                token: string;
                value: BigNumber;
                type: string;
            }>> 
    {
        return c.Transaction.getIncomingTransfer(this.connection, contractAddress, tokenAddresses, fromBlock, toBlock)
    }

    /**
     * Creates a safe transaction to execute all the transactions provided
     * @param transactions An array of the objects representing transactions.
     * @returns The updated enriched transaction-parameters
     */
    async batchSafeTransactions(
        transactions: IEnrichedTransactionParameters[]
    ): Promise <IEnrichedTransactionParameters> {
        return c.Transaction.batchSafeTransactions(transactions)
    }

    /**
     * Imports a transaction
     * @param exportableTxParams The exported transaction parameters
     * @returns The updated enriched transaction-parameters
     */
    async importTransaction(
        exportableTxParams: IExportableTransactionParameters,
    ): Promise <IEnrichedTransactionParameters> {
        return c.Transaction.importTransaction(this.connection, exportableTxParams)
    }

    /**
     * Sign as a contract with the contract signature format
     * @param enrichedTxParams The object representing the transaction.
     * @param signerContractAddress The signer's contract address
     * @returns The updated enriched transaction-parameters
     */
    async signAsContractSignature(
        enrichedTxParams: IEnrichedTransactionParameters,
        signerContractAddress: string
    ): Promise<IEnrichedTransactionParameters> {
        return c.Transaction.signAsContractSignature(enrichedTxParams, signerContractAddress)
    }

    /*

    Note: I haven't found any tests for this in the custody-lib

    async signInBunker(
        consentParams: IBunkerConsentParams,
        enrichedTxParams: IEnrichedTransactionParameters
    ): Promise<IEnrichedTransactionParameters> {
        return c.Transaction.signInBunker(consentParams, enrichedTxParams)
    }
    */

    /**
     * Creates the prepared transaction for a controller wallet given a privacy wallet transaction
     * @param controllerWalletAddress The address of the controller wallet address
     * @param privacyWalletTransaction The privacy wallet transaction
     * @returns The prepared transaction for a controller wallet
     */
    async createControllerTransactionFromPrivacyTransaction(
        controllerWalletAddress: string,
        privacyWalletTransaction: IEnrichedTransactionParameters
    ): Promise<IEnrichedTransactionParameters> {
        return c.Transaction.createControllerTransactionFromPrivacyTransaction(this.connection, controllerWalletAddress, privacyWalletTransaction)
    }
}

export default Transaction